import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api/client';
import { Outcome } from '../types';
import { SidePanel } from '../components/common/SidePanel';
import { useCsvActions } from '../context/CsvActionsContext';
import { useT } from '../i18n';
import { Target, Tag } from 'lucide-react';

interface OutcomeLabels {
  motivatedBy: string[];
  motivatedFor: string[];
}

function OutcomeCard({
  outcome,
  onSelect,
  onTagsChange,
}: {
  outcome: Outcome;
  onSelect: () => void;
  onTagsChange: (tags: string[]) => void;
}) {
  const { t } = useT();
  const [adding, setAdding] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !outcome.tags.includes(tag)) {
      onTagsChange([...outcome.tags, tag]);
    }
    setTagInput('');
    setAdding(false);
  };

  const removeTag = (tag: string) => {
    onTagsChange(outcome.tags.filter((t) => t !== tag));
  };

  return (
    <div className="inventory-card inventory-card--outcome">
      <button
        className="inventory-card-info"
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        title={t('inv.showDetails')}
      >
        i
      </button>

      <div className="inventory-card-body">
        <span className="inventory-card-title" title={outcome.title}>{outcome.title}</span>
      </div>

      <div className="inventory-card-tag-band">
        {outcome.tags.map((tag) => (
          <span key={tag} className="inventory-card-tag">
            {tag}
            <button
              className="inventory-card-tag-x"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
            >
              &times;
            </button>
          </span>
        ))}
        {!adding && (
          <button
            className="inventory-card-tag-add"
            onClick={(e) => { e.stopPropagation(); setAdding(true); }}
            title={t('inv.addTag')}
          >
            {outcome.tags.length === 0 ? <Tag className="icon-sm" /> : '+'}
          </button>
        )}
        {adding && (
          <div className="inventory-card-tag-input-wrap" onClick={(e) => e.stopPropagation()}>
            <input
              ref={inputRef}
              className="inventory-card-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                if (e.key === 'Escape') { setAdding(false); setTagInput(''); }
              }}
              onBlur={addTag}
              placeholder={t('inv.tagPlaceholder')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function InventoryOutcomesPage() {
  const { t } = useT();
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [labels, setLabels] = useState<OutcomeLabels>({ motivatedBy: [], motivatedFor: [] });
  const [loading, setLoading] = useState(true);
  const [selectedOutcome, setSelectedOutcome] = useState<Outcome | null>(null);
  const { setCsvActions } = useCsvActions();

  useEffect(() => {
    setCsvActions({
      downloadUrl: '/api/outcomes/export-csv',
      downloadLabel: t('csv.exportCsv'),
      secondaryDownloadUrl: '/api/outcomes/export-pdf',
      secondaryDownloadLabel: t('inv.exportPdf'),
    });
    return () => setCsvActions(null);
  }, [setCsvActions, t]);

  useEffect(() => {
    Promise.all([
      api.getOutcomes().then((data: any) => setOutcomes(data)),
      api.getOutcomeLabels().then((data: any) => setLabels(data)),
    ]).finally(() => setLoading(false));
  }, []);

  const handleTagsChange = async (outcomeId: number, tags: string[]) => {
    // Optimistic update
    setOutcomes((prev) =>
      prev.map((o) => (o.id === outcomeId ? { ...o, tags } : o))
    );
    if (selectedOutcome?.id === outcomeId) {
      setSelectedOutcome((prev) => prev ? { ...prev, tags } : prev);
    }
    try {
      await api.updateOutcomeTags(outcomeId, tags);
    } catch (err) {
      console.error('Failed to update tags', err);
      // Reload on failure
      const data = await api.getOutcomes() as Outcome[];
      setOutcomes(data);
    }
  };

  const outcomeGrid = useMemo(() => {
    const grid: Record<string, Record<string, Outcome[]>> = {};
    for (const mFor of labels.motivatedFor) {
      grid[mFor] = {};
      for (const mBy of labels.motivatedBy) {
        grid[mFor][mBy] = [];
      }
    }
    for (const outcome of outcomes) {
      if (grid[outcome.motivated_for]?.[outcome.motivated_by]) {
        grid[outcome.motivated_for][outcome.motivated_by].push(outcome);
      }
    }
    return grid;
  }, [outcomes, labels]);

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="flex items-center justify-center" style={{ height: 200 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (outcomes.length === 0 || labels.motivatedBy.length === 0 || labels.motivatedFor.length === 0) {
    return (
      <div className="inventory-page">
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Target /></div>
            <div className="empty-state-title">{t('inv.outcomes.emptyTitle')}</div>
            <div className="empty-state-desc">{t('inv.outcomes.emptyDesc')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="matrix-grid-container">
        <div
          className="matrix-grid"
          style={{ '--matrix-cols': `48px repeat(${labels.motivatedBy.length}, minmax(220px, 1fr))` } as React.CSSProperties}
        >
          <div className="matrix-grid-corner"></div>
          {labels.motivatedBy.map((mBy) => (
            <div key={mBy} className="matrix-grid-col">{mBy}</div>
          ))}

          {labels.motivatedFor.map((mFor) => (
            <React.Fragment key={mFor}>
              <div className="matrix-grid-row">{mFor}</div>
              {labels.motivatedBy.map((mBy) => (
                <div key={`${mFor}-${mBy}`} className="matrix-grid-cell matrix-grid-cell--wrap">
                  {outcomeGrid[mFor]?.[mBy]?.map((outcome) => (
                    <OutcomeCard
                      key={outcome.id}
                      outcome={outcome}
                      onSelect={() => setSelectedOutcome(outcome)}
                      onTagsChange={(tags) => handleTagsChange(outcome.id, tags)}
                    />
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <SidePanel
        open={!!selectedOutcome}
        onClose={() => setSelectedOutcome(null)}
        title={selectedOutcome?.title || ''}
      >
        {selectedOutcome && (
          <>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.motivatedBy')}</div>
              <div className="panel-detail-value">
                {selectedOutcome.motivated_by || '-'}
              </div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.motivatedFor')}</div>
              <div className="panel-detail-value">
                {selectedOutcome.motivated_for || '-'}
              </div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.description')}</div>
              <div className="panel-detail-value">
                {selectedOutcome.description || t('inv.noDescription')}
              </div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.tags')}</div>
              <div className="panel-detail-value">
                {selectedOutcome.tags.length > 0 ? (
                  <div className="panel-tags">
                    {selectedOutcome.tags.map((tag) => (
                      <span key={tag} className="badge">{tag}</span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">{t('inv.noTags')}</span>
                )}
              </div>
            </div>
          </>
        )}
      </SidePanel>
    </div>
  );
}
